import React, { useState } from 'react';
import styled from 'styled-components';
import Layout from '../../components/Layout';
import Container from '../../components/Container';
import Footer from '../../components/Footer';
import { FaChevronDown } from 'react-icons/fa';

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const HeroSection = styled.section`
  width: 100%;
  min-height: 300px;
  padding: 5rem 0 3rem 0;
  background: rgba(22, 32, 74, 0.9);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 0 0 24px 24px;
  position: relative;
  z-index: 2;
  margin-bottom: 3rem;
  border-bottom: 2px solid rgba(45, 108, 255, 0.3);
  
  @media (max-width: 768px) {
    min-height: 250px;
    padding: 4rem 0 2.5rem 0;
    margin-bottom: 2rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 1.1rem;
  letter-spacing: -1px;
  text-shadow: 0 0 16px #6ea8ff, 0 2px 10px rgba(0, 0, 0, 0.5);
  @media (max-width: 768px) { font-size: 1.7rem; }
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  color: #b6d0ff;
  max-width: 600px;
  margin: 0 auto;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 4rem 1.5rem;
  position: relative;
  z-index: 2;
`;

const RegionSection = styled.div`
  margin-bottom: 1.5rem;
  background: rgba(20, 30, 60, 0.7);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(45, 108, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(45, 108, 255, 0.4);
    box-shadow: 
      0 8px 24px rgba(45, 108, 255, 0.2),
      0 4px 12px rgba(45, 108, 255, 0.1);
  }
`;

const RegionHeader = styled.div`
  padding: 1.2rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(45, 108, 255, 0.1);
  }
`;

const RegionTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 24px;
    background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
    border-radius: 2px;
  }
`;

const ChevronIcon = styled(FaChevronDown)`
  color: #6ea8ff;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
`;

const RegionContent = styled.div`
  max-height: ${props => props.isOpen ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  padding: ${props => props.isOpen ? '1.5rem' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  transition: opacity 0.3s ease-in-out;
  background: rgba(10, 15, 46, 0.3);
`;

const WebsiteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const WebsiteCard = styled.div`
  background: rgba(30, 42, 74, 0.6);
  border: 1px solid rgba(45, 108, 255, 0.2);
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(45, 108, 255, 0.5);
    transform: translateY(-2px);
    background: rgba(30, 42, 74, 0.8);
    box-shadow: 0 4px 12px rgba(45, 108, 255, 0.2);
  }
`;

const WebsiteName = styled.h3`
  font-size: 1rem;
  color: #fff;
  margin: 0 0 0.5rem 0;
`;

const WebsiteUrl = styled.p`
  font-size: 0.85rem;
  color: #b6d0ff;
  margin: 0;
  word-break: break-all;
  opacity: 0.8;
`;

const SupportedWebsites = () => {
  const [openRegions, setOpenRegions] = useState({});

  const toggleRegion = (region) => {
    setOpenRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };

  const regions = {
    'United Kingdom': [
      { name: 'Google UK', url: 'https://www.google.co.uk' },
      { name: 'BBC', url: 'https://www.bbc.co.uk' },
      { name: 'Samsung UK', url: 'https://www.samsung.com/uk' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Amazon UK', url: 'https://www.amazon.co.uk' },
      { name: 'eBay UK', url: 'https://www.ebay.co.uk' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'TikTok', url: 'https://www.tiktok.com' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'The Guardian', url: 'https://www.theguardian.com' },
      { name: 'Daily Mail', url: 'https://www.dailymail.co.uk' },
      { name: 'The Telegraph', url: 'https://www.telegraph.co.uk' },
      { name: 'The Independent', url: 'https://www.independent.co.uk' },
      { name: 'The Sun', url: 'https://www.thesun.co.uk' },
      { name: 'NHS', url: 'https://www.nhs.uk' },
      { name: 'Gov UK', url: 'https://www.gov.uk' },
      { name: 'Sky Sports', url: 'https://www.skysports.com' },
      { name: 'ITV', url: 'https://www.itv.com' },
      { name: 'Channel 4', url: 'https://www.channel4.com' },
      { name: 'The Mirror', url: 'https://www.mirror.co.uk' },
      { name: 'The Express', url: 'https://www.express.co.uk' },
      { name: 'Sky', url: 'https://www.sky.com' },
      { name: 'Skyscanner', url: 'https://www.skyscanner.net' },
      { name: 'Metro', url: 'https://www.metro.co.uk' },
      { name: 'Tesco', url: 'https://www.tesco.com' },
      { name: 'Sainsbury\'s', url: 'https://www.sainsburys.co.uk' },
      { name: 'ASDA', url: 'https://www.asda.com' },
      { name: 'Argos', url: 'https://www.argos.co.uk' },
      { name: 'Currys', url: 'https://www.currys.co.uk' },
      { name: 'Marks & Spencer', url: 'https://www.marksandspencer.com' },
      { name: 'Nationwide', url: 'https://www.nationwide.co.uk' },
      { name: 'Barclays', url: 'https://www.barclays.co.uk' },
      { name: 'Lloyds Bank', url: 'https://www.lloydsbank.com' },
      { name: 'Santander', url: 'https://www.santander.co.uk' },
      { name: 'Halifax', url: 'https://www.halifax.co.uk' },
      { name: 'HSBC', url: 'https://www.hsbc.co.uk' },
      { name: 'Booking.com', url: 'https://www.booking.com' },
      { name: 'TripAdvisor UK', url: 'https://www.tripadvisor.co.uk' },
      { name: 'Rightmove', url: 'https://www.rightmove.co.uk' },
      { name: 'Auto Trader', url: 'https://www.autotrader.co.uk' }
    ],
    'United States': [
      { name: 'Google', url: 'https://www.google.com' },
      { name: 'Samsung US', url: 'https://www.samsung.com/us' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Amazon', url: 'https://www.amazon.com' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'Yahoo', url: 'https://www.yahoo.com' },
      { name: 'Netflix', url: 'https://www.netflix.com' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'eBay', url: 'https://www.ebay.com' },
      { name: 'Twitch', url: 'https://www.twitch.tv' },
      { name: 'CNN', url: 'https://www.cnn.com' },
      { name: 'New York Times', url: 'https://www.nytimes.com' },
      { name: 'Pinterest', url: 'https://www.pinterest.com' },
      { name: 'MSN', url: 'https://www.msn.com' },
      { name: 'Walmart', url: 'https://www.walmart.com' },
      { name: 'ESPN', url: 'https://www.espn.com' },
      { name: 'TikTok', url: 'https://www.tiktok.com' },
      { name: 'Apple', url: 'https://www.apple.com' },
      { name: 'Bing', url: 'https://www.bing.com' },
      { name: 'Hulu', url: 'https://www.hulu.com' },
      { name: 'IMDB', url: 'https://www.imdb.com' },
      { name: 'Best Buy', url: 'https://www.bestbuy.com' },
      { name: 'Target', url: 'https://www.target.com' },
      { name: 'Chase', url: 'https://www.chase.com' },
      { name: 'Bank of America', url: 'https://www.bankofamerica.com' },
      { name: 'Wells Fargo', url: 'https://www.wellsfargo.com' },
      { name: 'Roblox', url: 'https://www.roblox.com' },
      { name: 'Etsy', url: 'https://www.etsy.com' },
      { name: 'Zoom', url: 'https://www.zoom.us' },
      { name: 'DuckDuckGo', url: 'https://www.duckduckgo.com' },
      { name: 'Quora', url: 'https://www.quora.com' },
      { name: 'Indeed', url: 'https://www.indeed.com' },
      { name: 'Gmail', url: 'https://www.gmail.com' },
      { name: 'Outlook', url: 'https://www.outlook.com' },
      { name: 'USPS', url: 'https://www.usps.com' },
      { name: 'Craigslist', url: 'https://www.craigslist.org' },
      { name: 'Stack Overflow', url: 'https://www.stackoverflow.com' }
    ],
    'Australia': [
      { name: 'Google Australia', url: 'https://www.google.com.au' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Amazon Australia', url: 'https://www.amazon.com.au' },
      { name: 'News.com.au', url: 'https://www.news.com.au' },
      { name: 'Samsung Australia', url: 'https://www.samsung.com/au' },
      { name: 'Sydney Morning Herald', url: 'https://www.smh.com.au' },
      { name: 'ABC', url: 'https://www.abc.net.au' },
      { name: 'Real Estate', url: 'https://www.realestate.com.au' },
      { name: 'Domain', url: 'https://www.domain.com.au' },
      { name: 'Seek', url: 'https://www.seek.com.au' },
      { name: 'Airtasker', url: 'https://www.airtasker.com' },
      { name: 'Broadsheet', url: 'https://www.broadsheet.com.au' },
      { name: 'Courier Mail', url: 'https://www.couriermail.com.au' },
      { name: 'Herald Sun', url: 'https://www.heraldsun.com.au' },
      { name: 'The Age', url: 'https://www.theage.com.au' },
      { name: 'OzBargain', url: 'https://www.ozbargain.com.au' },
      { name: 'Woolworths', url: 'https://www.woolworths.com.au' },
      { name: 'Coles', url: 'https://www.coles.com.au' },
      { name: 'Qantas', url: 'https://www.qantas.com' },
      { name: 'Australia Post', url: 'https://www.auspost.com.au' },
      { name: 'iBoats', url: 'https://www.iboats.com.au' },
      { name: 'RACQ', url: 'https://www.racq.com.au' },
      { name: 'TripAdvisor Australia', url: 'https://www.tripadvisor.com.au' },
      { name: 'Bunnings', url: 'https://www.bunnings.com.au' },
      { name: 'Harvey Norman', url: 'https://www.harveynorman.com.au' },
      { name: 'MyHome', url: 'https://www.myhome.com.au' },
      { name: 'ABC Radio', url: 'https://www.abc.net.au/radio' },
      { name: 'Gumtree', url: 'https://www.gumtree.com.au' },
      { name: 'Macquarie', url: 'https://www.macquarie.com.au' },
      { name: 'Telstra', url: 'https://www.telstra.com.au' },
      { name: 'Optus', url: 'https://www.optus.com.au' },
      { name: 'Commonwealth Bank', url: 'https://www.commbank.com.au' },
      { name: 'NAB', url: 'https://www.nab.com.au' },
      { name: 'Westpac', url: 'https://www.westpac.com.au' },
      { name: 'ATO', url: 'https://www.ato.gov.au' },
      { name: 'SBS', url: 'https://www.sbs.com.au' },
      { name: '9News', url: 'https://www.9news.com.au' },
      { name: '7News', url: 'https://www.7news.com.au' },
      { name: 'Sky News', url: 'https://www.skynews.com.au' },
      { name: 'Fairfax', url: 'https://www.fairfax.com.au' },
      { name: 'Newsletters', url: 'https://www.newsletters.com.au' },
      { name: 'OzMovies', url: 'https://www.ozmovies.com.au' },
      { name: 'Trip', url: 'https://www.trip.com.au' }
      
    ],
    'Canada': [
      { name: 'Google Canada', url: 'https://www.google.ca' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Samsung Canada', url: 'https://www.samsung.com/ca' },
      { name: 'Amazon Canada', url: 'https://www.amazon.ca' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'TikTok', url: 'https://www.tiktok.com' },
      { name: 'CBC', url: 'https://www.cbc.ca' },
      { name: 'CTV News', url: 'https://www.ctvnews.ca' },
      { name: 'Global News', url: 'https://www.globalnews.ca' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'The Star', url: 'https://www.thestar.com' },
      { name: 'The Globe and Mail', url: 'https://www.theglobeandmail.com' },
      { name: 'National Post', url: 'https://www.nationalpost.com' },
      { name: 'Bell', url: 'https://www.bell.ca' },
      { name: 'Rogers', url: 'https://www.rogers.com' },
      { name: 'Telus', url: 'https://www.telus.com' },
      { name: 'Canada.ca', url: 'https://www.canada.ca' },
      { name: 'CIC', url: 'https://www.cic.gc.ca' },
      { name: 'CRA', url: 'https://www.canada.ca/en/revenue-agency.html' },
      { name: 'Service Canada', url: 'https://www.canada.ca/en/services.html' },
      { name: 'BMO', url: 'https://www.bmo.com' },
      { name: 'RBC', url: 'https://www.rbc.com' },
      { name: 'TD', url: 'https://www.td.com' },
      { name: 'Scotiabank', url: 'https://www.scotiabank.com' },
      { name: 'CIBC', url: 'https://www.cibc.com' },
      { name: 'President\'s Choice', url: 'https://www.presidentschoice.ca' },
      { name: 'Walmart Canada', url: 'https://www.walmart.ca' },
      { name: 'Best Buy Canada', url: 'https://www.bestbuy.ca' },
      { name: 'Home Depot', url: 'https://www.homedepot.ca' },
      { name: 'Costco', url: 'https://www.costco.ca' },
      { name: 'Loblaws', url: 'https://www.loblaw.ca' },
      { name: 'Metro', url: 'https://www.metro.ca' },
      { name: 'Air Canada', url: 'https://www.aircanada.com' },
      { name: 'WestJet', url: 'https://www.westjet.com' },
      { name: 'Kijiji', url: 'https://www.kijiji.ca' },
      { name: 'Canadian Tire', url: 'https://www.canadiantire.ca' },
      { name: 'MoneySense', url: 'https://www.moneysense.ca' },
      { name: 'Weather Canada', url: 'https://www.weather.gc.ca' },
      { name: 'Booking.com', url: 'https://www.booking.com' },
      { name: 'TripAdvisor Canada', url: 'https://www.tripadvisor.ca' }
    ],
    'Germany': [
      { name: 'Google Germany', url: 'https://www.google.de' },
      { name: 'Samsung Germany', url: 'https://www.samsung.com/de' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Amazon Germany', url: 'https://www.amazon.de' },
      { name: 'eBay Germany', url: 'https://www.ebay.de' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
      { name: 'T-Online', url: 'https://www.t-online.de' },
      { name: 'Der Spiegel', url: 'https://www.spiegel.de' },
      { name: 'Bild', url: 'https://www.bild.de' },
      { name: 'GMX', url: 'https://www.gmx.net' },
      { name: 'Web.de', url: 'https://www.web.de' },
      { name: 'ZDF', url: 'https://www.zdf.de' },
      { name: 'ARD', url: 'https://www.ard.de' },
      { name: 'Focus', url: 'https://www.focus.de' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'Twitch', url: 'https://www.twitch.tv' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'TikTok', url: 'https://www.tiktok.com' },
      { name: 'Süddeutsche', url: 'https://www.sueddeutsche.de' },
      { name: 'Otto', url: 'https://www.otto.de' },
      { name: 'Kicker', url: 'https://www.kicker.de' },
      { name: 'Chip', url: 'https://www.chip.de' },
      { name: 'Heise', url: 'https://www.heise.de' },
      { name: 'Deutsche Bahn', url: 'https://www.bahn.de' },
      { name: 'Tagesschau', url: 'https://www.tagesschau.de' },
      { name: 'PayPal', url: 'https://www.paypal.de' },
      { name: 'ImmobilienScout24', url: 'https://www.immobilienscout24.de' },
      { name: 'Netflix', url: 'https://www.netflix.de' },
      { name: 'IKEA', url: 'https://www.ikea.de' },
      { name: 'Booking.com', url: 'https://www.booking.com' },
      { name: 'Die Zeit', url: 'https://www.zeit.de' },
      { name: 'StepStone', url: 'https://www.stepstone.de' },
      { name: 'Bundesregierung', url: 'https://www.bundesregierung.de' },
      { name: 'Deutsche Post', url: 'https://www.deutschepost.de' },
      { name: 'DHL', url: 'https://www.dhl.de' },
      { name: 'BKA', url: 'https://www.bka.de' },
      { name: 'BSI', url: 'https://www.bsi.bund.de' },
      { name: 'Hochschule', url: 'https://www.hochschulkompass.de' },
      { name: 'Finanzamt', url: 'https://www.finanzamt.de' },
      { name: 'Arbeitsagentur', url: 'https://www.arbeitsagentur.de' },
      { name: 'Deezer', url: 'https://www.deezer.com' },
      { name: 'Spotify', url: 'https://www.spotify.de' },
    ],
    'Spain': [
      { name: 'Google Spain', url: 'https://www.google.es' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Amazon Spain', url: 'https://www.amazon.es' },
      { name: 'Samsung Spain', url: 'https://www.samsung.com/es' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'TikTok', url: 'https://www.tiktok.com' },
      { name: 'El Mundo', url: 'https://www.elmundo.es' },
      { name: 'El País', url: 'https://www.elpais.com' },
      { name: 'La Vanguardia', url: 'https://www.lavanguardia.com' },
      { name: 'ABC', url: 'https://www.abc.es' },
      { name: '20 Minutos', url: 'https://www.20minutos.es' },
      { name: 'RTVE', url: 'https://www.rtve.es' },
      { name: 'La Sexta', url: 'https://www.lasexta.com' },
      { name: 'Antena 3', url: 'https://www.antena3.com' },
      { name: 'Telecinco', url: 'https://www.telecinco.es' },
      { name: 'Marca', url: 'https://www.marca.com' },
      { name: 'AS', url: 'https://www.as.com' },
      { name: 'Sport', url: 'https://www.sport.es' },
      { name: 'Mundo Deportivo', url: 'https://www.mundodeportivo.com' },
      { name: 'BBVA', url: 'https://www.bbva.es' },
      { name: 'CaixaBank', url: 'https://www.caixabank.es' },
      { name: 'Santander', url: 'https://www.santander.es' },
      { name: 'Bankinter', url: 'https://www.bankinter.es' },
      { name: 'ING', url: 'https://www.ing.es' },
      { name: 'Renfe', url: 'https://www.renfe.es' },
      { name: 'SEPE', url: 'https://www.sepe.es' },
      { name: 'Agencia Tributaria', url: 'https://www.agenciatributaria.es' },
      { name: 'Seguridad Social', url: 'https://www.seg-social.es' },
      { name: 'BOE', url: 'https://www.boe.es' },
      { name: 'Ministerio de Sanidad', url: 'https://www.sanidad.gob.es' },
      { name: 'El Corte Inglés', url: 'https://www.elcorteingles.es' },
      { name: 'Zara', url: 'https://www.zara.com' },
      { name: 'Mercadona', url: 'https://www.mercadona.es' },
      { name: 'Carrefour', url: 'https://www.carrefour.es' },
      { name: 'Alcampo', url: 'https://www.alcampo.es' },
      { name: 'Booking.com', url: 'https://www.booking.com' },
      { name: 'TripAdvisor Spain', url: 'https://www.tripadvisor.es' },
    ],
    'France': [
      { name: 'Google France', url: 'https://www.google.fr' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Amazon France', url: 'https://www.amazon.fr' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Samsung France', url: 'https://www.samsung.com/fr' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'TikTok', url: 'https://www.tiktok.com' },
      { name: 'Le Monde', url: 'https://www.lemonde.fr' },
      { name: 'Le Figaro', url: 'https://www.lefigaro.fr' },
      { name: 'Ouest-France', url: 'https://www.ouest-france.fr' },
      { name: 'Libération', url: 'https://www.liberation.fr' },
      { name: 'France TV Info', url: 'https://www.francetvinfo.fr' },
      { name: 'BFMTV', url: 'https://www.bfmtv.com' },
      { name: 'TF1', url: 'https://www.tf1.fr' },
      { name: 'M6', url: 'https://www.6play.fr' },
      { name: 'Orange', url: 'https://www.orange.fr' },
      { name: 'SFR', url: 'https://www.sfr.fr' },
      { name: 'Free', url: 'https://www.free.fr' },
      { name: 'Bouygues Telecom', url: 'https://www.bouyguestelecom.fr' },
      { name: 'La Poste', url: 'https://www.laposte.fr' },
      { name: 'Impôts', url: 'https://www.impots.gouv.fr' },
      { name: 'Service Public', url: 'https://www.service-public.fr' },
      { name: 'Ameli', url: 'https://www.ameli.fr' },
      { name: 'CAF', url: 'https://www.caf.fr' },
      { name: 'SNCF', url: 'https://www.sncf.fr' },
      { name: 'Voyages SNCF', url: 'https://www.voyages-sncf.com' },
      { name: 'FNAC', url: 'https://www.fnac.com' },
      { name: 'Darty', url: 'https://www.darty.com' },
      { name: 'Cdiscount', url: 'https://www.cdiscount.com' },
      { name: 'Carrefour', url: 'https://www.carrefour.fr' },
      { name: 'Leclerc', url: 'https://www.e.leclerc' },
      { name: 'Monoprix', url: 'https://www.monoprix.fr' },
      { name: 'Lidl', url: 'https://www.lidl.fr' },
      { name: 'Uber Eats', url: 'https://www.ubereats.com' },
      { name: 'Doctolib', url: 'https://www.doctolib.fr' },
      { name: 'Decathlon', url: 'https://www.decathlon.fr' },
      { name: 'Zalando', url: 'https://www.zalando.fr' },
      { name: 'Booking.com', url: 'https://www.booking.com' },
      { name: 'TripAdvisor France', url: 'https://www.tripadvisor.fr' },
    ],
    'Ireland': [
      { name: 'Google Ireland', url: 'https://www.google.ie' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Amazon UK', url: 'https://www.amazon.co.uk' },
      { name: 'Samsung Ireland', url: 'https://www.samsung.com/ie' },
      { name: 'RTE', url: 'https://www.rte.ie' },
      { name: 'Independent.ie', url: 'https://www.independent.ie' },
      { name: 'Irish Times', url: 'https://www.irishtimes.com' },
      { name: 'Donegal Daily', url: 'https://www.donegaldaily.com' },
      { name: 'Sky Sports Ireland', url: 'https://www.skysports.com' },
      { name: 'JOE', url: 'https://www.joe.ie' },
      { name: 'MyHome', url: 'https://www.myhome.ie' },
      { name: 'Daft', url: 'https://www.daft.ie' },
      { name: 'Century 21', url: 'https://www.century21.ie' },
      { name: 'Gov.ie', url: 'https://www.gov.ie' },
      { name: 'Citizens Information', url: 'https://www.citizensinformation.ie' },
      { name: 'Irish Rail', url: 'https://www.irishrail.ie' },
      { name: 'Met Éireann', url: 'https://www.met.ie' },
      { name: 'Tesco Ireland', url: 'https://www.tesco.ie' },
      { name: 'SuperValu', url: 'https://www.supervalu.ie' },
      { name: 'eVillage', url: 'https://www.evillage.ie' },
      { name: 'Argos', url: 'https://www.argos.ie' },
      { name: 'Bonkers', url: 'https://www.bonkers.ie' },
      { name: 'CityPass', url: 'https://www.citypass.ie' },
      { name: 'Twinkl', url: 'https://www.twinkl.ie' },
      { name: 'Shop and Ship', url: 'https://www.shopandship.com' },
      { name: 'Courts', url: 'https://www.courts.ie' },
      { name: 'Gumtree', url: 'https://www.gumtree.ie' },
      { name: 'Taoiseach', url: 'https://www.taoiseach.gov.ie' },
      { name: 'Dublin Airport', url: 'https://www.dublinairport.com' },
      { name: 'Irish Statute Book', url: 'https://www.irishstatutebook.ie' },
      { name: 'The Local', url: 'https://www.thelocal.ie' },
      { name: 'Donegal Now', url: 'https://www.donegalnow.com' },
      { name: 'Sporting Life Ireland', url: 'https://www.sportinglife.ie' },
      { name: 'Citibank', url: 'https://www.citibank.ie' },
      { name: 'Bank of Ireland', url: 'https://www.bankofireland.com' },
      { name: 'AIB', url: 'https://www.aib.ie' },
      { name: 'Pernod Ricard', url: 'https://www.pernod-ricard.com' },
      { name: 'Irish Water', url: 'https://www.water.ie' },
      { name: 'Entertainment.ie', url: 'https://www.entertainment.ie' },
      { name: 'Irish Mirror', url: 'https://www.irishmirror.ie' },
      { name: 'Bord Bia', url: 'https://www.bordbia.ie' },
      
    ],
    'India': [
      { name: 'Google India', url: 'https://www.google.co.in' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Amazon India', url: 'https://www.amazon.in' },
      { name: 'Flipkart', url: 'https://www.flipkart.com' },
      { name: 'Samsung India', url: 'https://www.samsung.com/in' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'India Times', url: 'https://www.indiatimes.com' },
      { name: 'NDTV', url: 'https://www.ndtv.com' },
      { name: 'Times of India', url: 'https://www.timesofindia.indiatimes.com' },
      { name: 'Rediff', url: 'https://www.rediff.com' },
      { name: 'IRCTC', url: 'https://www.irctc.co.in' },
      { name: 'Swiggy', url: 'https://www.swiggy.com' },
      { name: 'Zomato', url: 'https://www.zomato.com' },
      { name: 'Make in India', url: 'https://www.makeinindia.com' },
      { name: 'Naukri', url: 'https://www.naukri.com' },
      { name: 'JustDial', url: 'https://www.justdial.com' },
      { name: 'Snapdeal', url: 'https://www.snapdeal.com' },
      { name: 'Myntra', url: 'https://www.myntra.com' },
      { name: 'OLX', url: 'https://www.olx.in' },
      { name: 'PolicyBazaar', url: 'https://www.policybazaar.com' },
      { name: 'OYO', url: 'https://www.oyorooms.com' },
      { name: 'Cleartrip', url: 'https://www.cleartrip.com' },
      { name: 'Investing.com India', url: 'https://www.investing.com/india' },
      { name: 'Business Today', url: 'https://www.businesstoday.in' },
      { name: 'Coursera', url: 'https://www.coursera.org' },
      { name: 'UPSC', url: 'https://www.upsc.gov.in' },
      { name: 'India Post', url: 'https://www.indiapost.gov.in' },
      { name: 'SBI', url: 'https://www.sbi.co.in' },
      { name: 'ICICI', url: 'https://www.icici.com' },
      { name: 'Axis Bank', url: 'https://www.axisbank.com' },
      { name: 'HDFC Bank', url: 'https://www.hdfcbank.com' },
      { name: 'LIC', url: 'https://www.lic.in' },
      { name: 'Indian Railways', url: 'https://www.indianrailways.gov.in' },
      { name: 'IndiaMART', url: 'https://www.indiamart.com' },
      { name: 'Paytm', url: 'https://www.paytm.com' },
      { name: 'GoDaddy', url: 'https://www.godaddy.com' },
      { name: 'Hotstar', url: 'https://www.hotstar.com' },
      { name: 'Gaana', url: 'https://www.gaana.com' },
      { name: 'Jagran Josh', url: 'https://www.jagranjosh.com' },
      { name: 'BookMyShow', url: 'https://www.bookmyshow.com' },
      { name: 'Tata Cliq', url: 'https://www.tatacliq.com' },
      
    ],
    'Italy': [
      { name: 'Google Italy', url: 'https://www.google.it' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Amazon Italy', url: 'https://www.amazon.it' },
      { name: 'Samsung Italy', url: 'https://www.samsung.com/it' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'TikTok', url: 'https://www.tiktok.com' },
      { name: 'Corriere', url: 'https://www.corriere.it' },
      { name: 'Repubblica', url: 'https://www.repubblica.it' },
      { name: 'Il Sole 24 Ore', url: 'https://www.ilsole24ore.com' },
      { name: 'Il Fatto Quotidiano', url: 'https://www.ilfattoquotidiano.it' },
      { name: 'TGCOM24', url: 'https://www.tgcom24.mediaset.it' },
      { name: 'Rai News', url: 'https://www.rainews.it' },
      { name: 'Il Giornale', url: 'https://www.ilgiornale.it' },
      { name: 'La Stampa', url: 'https://www.lastampa.it' },
      { name: 'Libero Quotidiano', url: 'https://www.liberoquotidiano.it' },
      { name: 'ANSA', url: 'https://www.ansa.it' },
      { name: 'RAI', url: 'https://www.rai.it' },
      { name: 'Mediaset', url: 'https://www.mediaset.it' },
      { name: 'Sky', url: 'https://www.sky.it' },
      { name: 'RaiPlay', url: 'https://www.raiplay.it' },
      { name: 'Treccani', url: 'https://www.treccani.it' },
      { name: 'Poste Italiane', url: 'https://www.poste.it' },
      { name: 'Agenzia Entrate', url: 'https://www.agenziaentrate.gov.it' },
      { name: 'INPS', url: 'https://www.inps.it' },
      { name: 'PagoPA', url: 'https://www.pagopa.gov.it' },
      { name: 'Istruzione', url: 'https://www.istruzione.it' },
      { name: 'Universitaly', url: 'https://www.universitaly.it' },
      { name: 'Trenitalia', url: 'https://www.trenitalia.com' },
      { name: 'Italo', url: 'https://www.italotreno.it' },
      { name: 'Booking.com', url: 'https://www.booking.com' },
      { name: 'TripAdvisor Italy', url: 'https://www.tripadvisor.it' },
      { name: 'Subito', url: 'https://www.subito.it' },
      { name: 'Idealista', url: 'https://www.idealista.it' },
      { name: 'Zalando', url: 'https://www.zalando.it' },
      { name: 'Esselunga', url: 'https://www.esselunga.it' },
      { name: 'Coop', url: 'https://www.coop.it' },
      { name: 'Euronics', url: 'https://www.euronics.it' },
      { name: 'MediaWorld', url: 'https://www.mediaworld.it' }
    ],
    'Netherlands': [
      { name: 'Google Netherlands', url: 'https://www.google.nl' },
      { name: 'Amazon Netherlands', url: 'https://www.amazon.nl' },
      { name: 'NU.nl', url: 'https://www.nu.nl' },
      { name: 'Samsung Netherlands', url: 'https://www.samsung.com/nl' },
      { name: 'Spotify', url: 'https://www.spotify.nl' },
      { name: 'YouTube', url: 'https://www.youtube.com' },
      { name: 'Facebook', url: 'https://www.facebook.com' },
      { name: 'De Telegraaf', url: 'https://www.telegraaf.nl' },
      { name: 'AD', url: 'https://www.ad.nl' },
      { name: 'RTL Nieuws', url: 'https://www.rtlnieuws.nl' },
      { name: 'NOS', url: 'https://www.nos.nl' },
      { name: 'Marktplaats', url: 'https://www.marktplaats.nl' },
      { name: 'Bol.com', url: 'https://www.bol.com' },
      { name: 'Wehkamp', url: 'https://www.wehkamp.nl' },
      { name: 'Coolblue', url: 'https://www.coolblue.nl' },
      { name: 'Zalando', url: 'https://www.zalando.nl' },
      { name: 'ING', url: 'https://www.ing.nl' },
      { name: 'Rabobank', url: 'https://www.rabobank.nl' },
      { name: 'ABN AMRO', url: 'https://www.abnamro.nl' },
      { name: 'Tweakers', url: 'https://www.tweakers.net' },
      { name: 'Belastingdienst', url: 'https://www.belastingdienst.nl' },
      { name: '9292', url: 'https://www.9292.nl' },
      { name: 'NS', url: 'https://www.ns.nl' },
      { name: 'Albert Heijn', url: 'https://www.ah.nl' },
      { name: 'Jumbo', url: 'https://www.jumbo.com' },
      { name: 'Thuisbezorgd', url: 'https://www.thuisbezorgd.nl' },
      { name: 'Booking.com', url: 'https://www.booking.com' },
      { name: 'Funda', url: 'https://www.funda.nl' },
      { name: 'Pathé', url: 'https://www.pathe.nl' },
      { name: 'Instagram', url: 'https://www.instagram.com' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com' },
      { name: 'Twitter', url: 'https://www.twitter.com' },
      { name: 'Reddit', url: 'https://www.reddit.com' },
      { name: 'DUO', url: 'https://www.duo.nl' },
      { name: 'MijnOverheid', url: 'https://www.mijnoverheid.nl' },
      { name: 'Kijk', url: 'https://www.kijk.nl' },
      { name: 'Videoland', url: 'https://www.videoland.com' },
      { name: 'PostNL', url: 'https://www.postnl.nl' },
      { name: 'Gemeente', url: 'https://www.gemeente.nl' },
      { name: 'Startpagina', url: 'https://www.startpagina.nl' },
      { name: 'Trouw', url: 'https://www.trouw.nl' },
      { name: 'Het Parool', url: 'https://www.parool.nl' },
      { name: 'MetroNieuws', url: 'https://www.metronieuws.nl' },
      { name: 'Schoolbank', url: 'https://www.schoolbank.nl' },
      { name: 'Tinder', url: 'https://www.tinder.com' },
      { name: 'Holland & Barrett', url: 'https://www.hollandandbarrett.nl' },
      { name: 'Gamma', url: 'https://www.gamma.nl' },
      { name: 'Praxis', url: 'https://www.praxis.nl' },
      { name: 'KLM', url: 'https://www.klm.com' },
      { name: 'Transavia', url: 'https://www.transavia.com' },
      { name: 'Schiphol', url: 'https://www.schiphol.nl' },
      { name: 'UWV', url: 'https://www.uwv.nl' },
      { name: 'Indeed', url: 'https://www.indeed.nl' },
      { name: 'Intermediair', url: 'https://www.intermediair.nl' },
      { name: 'Manpower', url: 'https://www.manpower.nl' },
      { name: 'Randstad', url: 'https://www.randstad.nl' },
      { name: 'Tempo-Team', url: 'https://www.tempoteam.nl' },
      { name: 'Centrum Veilig Wonen', url: 'https://www.centrumveiligwonen.nl' },
      { name: 'CBR', url: 'https://www.cbr.nl' },
      { name: 'ANWB', url: 'https://www.anwb.nl' },
      { name: 'RDW', url: 'https://www.rdw.nl' },
      { name: 'Rijksoverheid', url: 'https://www.rijksoverheid.nl' },
      { name: 'KVK', url: 'https://www.kvk.nl' },
      { name: 'PsyQ', url: 'https://www.psyq.nl' },
      { name: 'Thuisarts', url: 'https://www.thuisarts.nl' },
      { name: 'Zorgkaart Nederland', url: 'https://www.zorgkaartnederland.nl' },
      { name: 'Zilveren Kruis', url: 'https://www.zilverenkruis.nl' },
      { name: 'FNV', url: 'https://www.fnv.nl' },
      { name: 'Consumentenbond', url: 'https://www.consumentenbond.nl' },
      { name: 'AutoWeek', url: 'https://www.autoweek.nl' },
      { name: 'AutoTrack', url: 'https://www.autotrack.nl' },
      { name: 'GasPedaal', url: 'https://www.gaspedaal.nl' },
      { name: 'MeteoVista', url: 'https://www.meteovista.nl' },
      { name: 'Buienradar', url: 'https://www.buienradar.nl' },
      { name: 'Weeronline', url: 'https://www.weeronline.nl' },
      { name: 'Startpunt Onderwijs', url: 'https://www.startpuntonderwijs.nl' },
      { name: 'Encyclo', url: 'https://www.encyclo.nl' }
    ]
  };

  return (
    <Layout>
      <PageWrapper>
        <BackgroundPattern />
        <HeroSection>
          <HeroTitle>Initial Warming Session Websites</HeroTitle>
          <HeroSubtitle>
            Deserved Warmer supports a wide range of websites across different regions for the initial warming session.
            Click on a region to view its supported websites.
          </HeroSubtitle>
        </HeroSection>
        <ContentContainer>
          {Object.entries(regions).map(([region, websites]) => (
            <RegionSection key={region}>
              <RegionHeader onClick={() => toggleRegion(region)}>
                <RegionTitle>{region}</RegionTitle>
                <ChevronIcon isOpen={openRegions[region]} />
              </RegionHeader>
              <RegionContent isOpen={openRegions[region]}>
                <WebsiteGrid>
                  {websites.map((website, index) => (
                    <WebsiteCard key={index}>
                      <WebsiteName>{website.name}</WebsiteName>
                      <WebsiteUrl>{website.url}</WebsiteUrl>
                    </WebsiteCard>
                  ))}
                </WebsiteGrid>
              </RegionContent>
            </RegionSection>
          ))}
        </ContentContainer>
        <Footer />
      </PageWrapper>
    </Layout>
  );
};

export default SupportedWebsites; 